import type { ActionTree, MutationTree, GetterTree } from 'vuex'
import type RootState from '@web/store/types'
import type { Community, CommunityState, UserCommunity } from '@club/types/Community'

const state: CommunityState = {
  selectedCommunity: null
}

// Подписки берём готовыми из платформы, как платформенный getType
// (store/modules/community.ts): activeSubscriptions — уже отфильтрованные активные
// (receiveActiveSubscriptions), hasSubscription — есть ли активная подписка вообще.
// Своей фильтрации по isActive/isLifetime здесь нет: правило активности живёт
// в одном месте — в subscription store.
// Список привязанных клубов лежит внутри raw.content: геттер subscription
// не поднимает content на верхний уровень (см. getSubscription). Раньше его читали
// как subscription.content — на клубе с is_optional_purchase это падало с TypeError
// («Не получилось загрузить информацию о подписках»).
interface CommunitySubscription {
  raw?: { content?: { communities?: Array<{ id: number }> } };
}

interface SubscriptionAccess {
  activeSubscriptions: CommunitySubscription[];
  hasSubscription: boolean;
}

// Форма платформенного store community: Mapper.converter переводит ответ API в camelCase,
// а даты userCommunity — в миллисекунды (mapper/CommunityMapper.ts).
interface PlatformUserCommunity {
  communityId: number;
  isOptionalPurchase?: boolean;
  startDate?: number;
  endDate?: number;
  paymentSubscriptionId?: number | null;
  [key: string]: unknown;
}

interface PlatformCommunity {
  id: number;
  slug: string;
  userCommunity?: PlatformUserCommunity;
  [key: string]: unknown;
}

const SNAKE_CASE_FIELDS: Array<[string, string]> = [
  ['external_id', 'externalId'],
  ['custom_url', 'customUrl'],
  ['landing_id', 'landingId'],
  ['subscribe_title', 'subscribeTitle'],
  ['subscribe_description', 'subscribeDescription'],
  ['payment_subscription_id', 'paymentSubscriptionId'],
  ['faq_url', 'faqUrl'],
  ['have_subscription_landing_url', 'haveSubscriptionLandingUrl'],
  ['no_have_subscription_landing_url', 'noHaveSubscriptionLandingUrl'],
  ['telegram_chat_channel_id', 'telegramChatChannelId'],
  ['telegram_chat_url', 'telegramChatUrl'],
  ['telegram_comments_channel_id', 'telegramCommentsChannelId'],
  ['course_ids', 'courseIds']
]

// Клубные компоненты перенесены из community-frontend и читают поля в snake_case,
// а платформенный store хранит их в camelCase. Раскладываем обратно, чтобы не
// переписывать все клубные шаблоны и не заводить второй источник данных.
function toSnakeCase<T> (source: Record<string, unknown>): T {
  const result: Record<string, unknown> = { ...source }
  for (const [snake, camel] of SNAKE_CASE_FIELDS) {
    if (camel in source) {
      result[snake] = source[camel]
    }
  }
  return result as T
}

function toClubUserCommunity (userCommunity: PlatformUserCommunity): UserCommunity {
  const mapped = toSnakeCase<UserCommunity>(userCommunity as unknown as Record<string, unknown>)
  return {
    ...mapped,
    community_id: userCommunity.communityId,
    is_optional_purchase: Boolean(userCommunity.isOptionalPurchase),
    // даты платформа уже отдаёт в миллисекундах — наружу они не используются,
    // нужны только getType ниже
    start_date: userCommunity.startDate,
    end_date: userCommunity.endDate
  }
}

function toClubCommunity (community: PlatformCommunity): Community {
  const mapped = toSnakeCase<Community>(community as unknown as Record<string, unknown>)
  if (community.userCommunity) {
    mapped.userCommunity = toClubUserCommunity(community.userCommunity)
  }
  return mapped
}

// Клубная семантика типа отличается от платформенной (у платформы есть ещё 'hidden',
// и клуб без userCommunity у неё не 'recommended'), поэтому тип считаем здесь,
// а из платформы берём только данные.
function getType (community: Community, { activeSubscriptions, hasSubscription }: SubscriptionAccess): 'recommended' | 'owned' | 'expired' {
  if (!community.userCommunity) {
    return 'recommended'
  }
  // Если клуб куплен через встроенную покупку, смотрим на подписку: сначала есть ли
  // активная вообще, затем — покрывает ли она именно этот клуб (как платформенный getType).
  if (community.userCommunity?.is_optional_purchase) {
    if (!hasSubscription) {
      return 'expired'
    }
    const hasCommunity = activeSubscriptions.find(
      s => s.raw?.content?.communities?.some(c => c.id === community?.userCommunity?.community_id)
    )
    return hasCommunity ? 'owned' : 'expired'
  }

  const now = new Date().getTime()
  const startDate = Number(community.userCommunity?.start_date) || 0
  if (now < startDate) {
    return 'recommended'
  }

  const endDate = Number(community.userCommunity?.end_date) || 0
  if (now > startDate && now < endDate) {
    return 'owned'
  }

  return 'expired'
}

const getters: GetterTree<CommunityState, RootState> = {
  // Источник данных — платформенный store community (те же ручки /community и
  // /user-community, которые клуб раньше запрашивал сам). Клуб их не дублирует.
  rawCommunities: (state, getters, rootState, rootGetters): Community[] => {
    const access: SubscriptionAccess = {
      activeSubscriptions: rootGetters['subscription/activeSubscriptions'] || [],
      hasSubscription: Boolean(rootGetters['subscription/hasSubscription'])
    }
    const platformCommunities: PlatformCommunity[] = rootGetters['community/communities'] || []

    return platformCommunities.map(community => {
      const _community = toClubCommunity(community)
      return {
        ..._community,
        type: getType(_community, access)
      }
    })
  },
  communities: (state, { rawCommunities }): Community[] => rawCommunities.filter((c: Community) => c.userCommunity),
  getCommunityBySlug: (state, { rawCommunities }) => (slug: string): Community | undefined =>
    rawCommunities.find((c: Community) => c.slug === slug),
  selectedCommunity: ({ selectedCommunity }): Community | null => selectedCommunity,
  hasSubscribe: (state, { communities }): boolean => communities.some((c: Community) => c.type === 'owned')
}

const mutations: MutationTree<CommunityState> = {
  setSelectedCommunity (state: CommunityState, community: Community | null) {
    state.selectedCommunity = community
  },
  logout (state: CommunityState) {
    state.selectedCommunity = null
  }
}

// Загрузку делает платформенный store: он дедуплицирует параллельные вызовы и
// уже стартует в composition/useApp. Клубные действия остаются точками входа
// для перенесённого кода, но своих запросов не делают.
const actions: ActionTree<CommunityState, RootState> = {
  async fetchCommunities ({ dispatch }) {
    await dispatch('community/fetchCachedCommunities', null, { root: true })
  },
  async fetchUserCommunities ({ dispatch }) {
    await dispatch('community/fetchCachedCommunities', null, { root: true })
  },
  async fetchCommunitiesCached ({ dispatch }) {
    await dispatch('community/fetchCachedCommunities', null, { root: true })
  }
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
}
